import { useMemo } from 'react';
import cn from 'classnames';
import { BsSquareFill } from 'react-icons/bs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Importance } from '@blisspointmedia/bpm-types/dist/Creative';
import { ChartContainer } from '@Components/ChartContainer';
import { Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { toImportanceShares } from './attributes-chart-utils';
import { CreativeInsightsData, RawDataType } from './creative-insights-constants';
import { RawDataDownloadButton } from './RawDataDownloadButton';
import './AttributesChart.scss';

interface AttributesChartProps {
  data: CreativeInsightsData;
  selectedAttribute: string;
  onAttributeClick: (attributeName: string) => void;
  kpi: string;
}

export const AttributesChart = ({
  data: { attributeImportanceData, suppressedAttributes, fillColors },
  selectedAttribute,
  onAttributeClick,
  kpi
}: AttributesChartProps) => {
  const shares = useMemo(
    () => toImportanceShares(attributeImportanceData, suppressedAttributes),
    [attributeImportanceData, suppressedAttributes]
  );

  return (
    <ChartContainer
      className='attributes-chart-container'
      title={
        <>
          Attributes
          <InfoTooltip size={Size.MD}>
            {`The attribute importance scores reflect the fraction of total creative-driven variation in
  KPI outcomes accounted for by a given attribute. Creative elements will drive some portion
  of total KPI variation – media would also drive a substantial amount – and the attribute
  importances tell us how much of that variation is driven by a given creative element.`}
          </InfoTooltip>
        </>
      }
      rightActions={
        <RawDataDownloadButton
          dataType={RawDataType.ATTRIBUTE}
          kpi={kpi}
          fileName='linear_creative_importances'
          nameColumn='Attribute'
          valueColumn='Importance'
          subject='attribute importances'
        />
      }
    >
      <div className='attributes-container'>
        <div className='attributes-content'>
          <div className='attribute-names'>
            <div className='attribute-names-list' data-testid='attributes-list'>
              {shares.map(attribute => (
                <AttributeListItem
                  key={attribute.name}
                  attribute={attribute}
                  color={fillColors.get(attribute.name)}
                  active={selectedAttribute === attribute.name}
                  onClick={onAttributeClick}
                />
              ))}
            </div>
          </div>
          <div className='attributes-chart'>
            <ResponsiveContainer initialDimension={{ width: 1, height: 1 }}>
              <PieChart>
                <Tooltip content={<AttributesTooltip />} />
                <Pie
                  data={shares}
                  innerRadius='50%'
                  dataKey='val'
                  onClick={field => onAttributeClick(String(field.name))}
                  cursor='pointer'
                >
                  {shares.map(entry => (
                    <Cell key={entry.name} fill={fillColors.get(entry.name)} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </ChartContainer>
  );
};

interface AttributeListItemProps {
  attribute: Importance;
  color: string | undefined;
  active: boolean;
  onClick: (attributeName: string) => void;
}

const AttributeListItem = ({ attribute, color, active, onClick }: AttributeListItemProps) => (
  <InteractiveDiv
    className={cn('attribute', { active })}
    data-testid='attribute-item'
    onClick={() => onClick(attribute.name)}
  >
    <div className='color'>
      <BsSquareFill fill={color} />
    </div>
    <div className='name-and-value'>
      <b className='name'>{attribute.name}</b>
      {` — ${attribute.val}%`}
    </div>
  </InteractiveDiv>
);

interface AttributesTooltipProps {
  payload?: { name?: string }[];
}

const AttributesTooltip = ({ payload }: AttributesTooltipProps) => {
  if (!payload?.length) return null;
  return <div className='attributes-chart-tooltip'>{`Attribute: ${payload[0].name}`}</div>;
};
